<template>
    <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="dataTableFooterProps"
        :loading="isBusy"
        :expanded.sync="expanded"
        item-key="id"
        show-expand
    >
        <template v-slot:top>
            <v-toolbar flat color="white">
                <v-text-field
                    v-model="filter"
                    prepend-inner-icon="mdi-magnify"
                    :placeholder="$t('common.filter')"
                    outlined
                    dense
                    hide-details
                />
            </v-toolbar>
        </template>
        <template v-slot:item.id="{ item }">
            {{ item.id }}
        </template>
        <template v-slot:item.state="{ item }">
            <v-chip small :color="stateColor(item.state)">
                {{ $t("bulk_orders.states." + item.state) }}
            </v-chip>
        </template>
        <template v-slot:item.created_at="{ item }">
            <span class="text-no-wrap">
                {{ $moment(item.created_at).format('LLL') }}
            </span>
        </template>
        <template v-slot:item.orders="{ item }">
            <span class="text-no-wrap">
              {{ item.orders.length }} {{ $t('products.orders') }}
            </span>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-5">
                <p>{{ $t('orders.orders_in_bulk_order') }}</p>
                <orders-table :url="`/api/orders/by-bulk-order/` + item.id"
                              :id="`orders-table-for-bulk-order-` + item.id"
                              hide-actions
                />
            </td>
        </template>
        <template v-slot:item.actions="{ item }">
            <div class="text-no-wrap">
                <v-btn @click="onDetail(item)" :disabled="isBusy" icon>
                    <v-icon>mdi-magnify</v-icon>
                </v-btn>
                <v-btn v-if="isApprover" @click="onEdit(item)" :disabled="isBusy" icon>
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn :disabled="isBusy || item.state != 'WAITING_FOR_APPROVAL'" @click="onDelete(item)" color="error" icon>
                    <v-icon>mdi-trash-can</v-icon>
                </v-btn>
            </div>
        </template>
    </v-data-table>
</template>

<script>
import SnackbarMixin from '@/mixins/SnackbarMixin'
import DataTableMixin from '@/mixins/DataTableMixin'
import {API} from '@/api/api'
import _ from 'lodash'
import OrdersTable from '@/components/user/orders/OrdersTable'

export default {
    name: "BulkOrdersTable",
    components: {OrdersTable},
    mixins: [SnackbarMixin, DataTableMixin],
    props: {
        url: {
            type: String,
            required: true
        },
        includingApproved: {
            type: Boolean,
            required: false,
            default: () => false
        }
    },
    data: () => ({
        // loading
        isBusyBulkOrders: false,
        isBusyDelete: false,
        // pagination metadata
        total: 0,
        // data table items
        items: [],
        // table filter
        filter: "",
        // data table options
        options: {
            page: 1,
            itemsPerPage: 15,
            sortBy: ["created_at"],
            sortDesc: [true]
        },
        // expanded
        expanded: []
    }),
    watch: {
        options: {
            handler() {
                this.fetchBulkOrders()
            },
            deep: true,
        },
        filter: {
            handler: _.debounce(function () {
                this.resetServerTablePage(this.fetchBulkOrders);
            }, 250)
        }
    },
    computed: {
        isBusy() {
            return this.isBusyBulkOrders || this.isBusyDelete;
        },
        headers() {
            return [
                {
                    text: '#',
                    value: 'id'
                },
                {
                    text: this.$t('orders.state'),
                    value: 'state',
                    sortable: false
                },
                {
                    text: this.$t('orders.created_at'),
                    value: 'created_at'
                },
                {
                    text: this.$t('orders.name'),
                    value: 'name'
                },
                {
                    text: this.$t('orders.orders'),
                    value: 'orders',
                    sortable: false
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'end'
                }
            ];
        },
        isApprover() {
            let roles = this.$store.getters['identity/getRoles'];
            return roles && roles.includes('ROLE_APPROVER');
        }
    },
    methods: {
        fetchBulkOrders() {
            const {sortBy, sortDesc, page, itemsPerPage} = this.options;
            const sortMap = {
                created_at: "createdAt"
            };
            const url = this.url
                + "?page=" + (page ?? 1)
                + "&itemsPerPage=" + (itemsPerPage ?? 10)
                + "&sortBy=" + this.mapServerSort(sortBy, sortMap, "createdAt")
                + "&sortDesc=" + (_.isArray(sortDesc) ? (sortDesc[0] ? "true" : "false") : "false")
                + "&includingApproved=" + (this.includingApproved ? "true" : "false")
                + (this.filter ? "&filter=" + encodeURIComponent(this.filter) : "");
            this.isBusyBulkOrders = true;
            API.get(url)
                .then(response => {
                    this.total = response.data.total;
                    this.items = response.data.items;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyBulkOrders = false;
                });
        },
        onDetail(item) {
            this.$router.push({name: 'bulk-order', params: {bulkOrderId: item.id}});
        },
        onEdit(item) {
            this.$router.push({name: 'edit-bulk-order', params: {bulkOrderId: item.id}})
        },
        onDelete(item) {
            if (!confirm(this.$t('confirm.delete_bulk_order'))) {
                return;
            }
            this.isBusyDelete = true;
            API.delete("/api/bulk-orders/" + item.id)
                .then(() => {
                    this.fetchBulkOrders();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyDelete = false;
                });
        },
        stateColor(state) {
            switch (state) {
                case "CREATED":
                    return "info";
                case "WAITING_FOR_APPROVAL":
                case "IN_PROGRESS":
                    return "warning";
                case "CLOSED":
                    return "success";
                default:
                    return "secondary";
            }
        }
    },
    created() {
        this.fetchBulkOrders();
    }
}
</script>
